import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  TrendingUp, Star, Truck, Clock, Award, RefreshCw,
  ChevronDown, ChevronUp, User, Target, Trophy
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DriverPerformanceWidget({ isAdmin = true }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('week');
  const [expandedDriver, setExpandedDriver] = useState(null);

  const fetchPerformance = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const endpoint = isAdmin 
        ? `/api/driver-tracking/performance/all-drivers?period=${period}`
        : `/api/driver-tracking/performance/my-stats?period=${period}`;
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error('Failed to fetch performance:', error);
    } finally {
      setLoading(false);
    }
  }, [period, isAdmin]);

  useEffect(() => {
    fetchPerformance();
  }, [fetchPerformance]);

  const getRatingStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star 
        key={i}
        className={`h-3 w-3 ${i < Math.floor(rating) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
      />
    ));
  };

  const getPerformanceBadge = (score) => {
    if (score >= 90) return { label: 'Excellent', color: 'bg-green-100 text-green-700' };
    if (score >= 75) return { label: 'Good', color: 'bg-blue-100 text-blue-700' };
    if (score >= 60) return { label: 'Average', color: 'bg-yellow-100 text-yellow-700' };
    return { label: 'Needs Improvement', color: 'bg-red-100 text-red-700' };
  };

  if (loading) {
    return (
      <Card className="h-full" data-testid="driver-performance-widget">
        <CardContent className="flex items-center justify-center h-48">
          <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  // Admin view - all drivers
  if (isAdmin && data?.drivers) {
    return (
      <Card className="h-full overflow-hidden" data-testid="driver-performance-widget">
        <CardHeader className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white py-3 px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              <CardTitle className="text-base font-bold">Driver Performance</CardTitle>
            </div>
            <button onClick={fetchPerformance} className="p-1 hover:bg-white/20 rounded">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          <div className="flex gap-2 mt-2">
            {['week', 'month', 'all'].map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-2 py-0.5 text-xs rounded-full transition-colors ${
                  period === p 
                    ? 'bg-white text-indigo-700' 
                    : 'bg-white/20 hover:bg-white/30'
                }`}
              >
                {p === 'week' ? 'This Week' : p === 'month' ? 'This Month' : 'All Time'}
              </button>
            ))}
          </div>
        </CardHeader>

        <CardContent className="p-3 space-y-3 max-h-96 overflow-y-auto">
          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="p-2 bg-indigo-50 rounded-lg">
              <p className="text-lg font-bold text-indigo-700">{data.summary?.total_deliveries || 0}</p>
              <p className="text-[10px] text-slate-500">Total Deliveries</p>
            </div>
            <div className="p-2 bg-yellow-50 rounded-lg">
              <div className="flex items-center justify-center gap-1">
                <Star className="h-4 w-4 text-yellow-400 fill-current" />
                <span className="text-lg font-bold text-yellow-700">{data.summary?.average_rating || 0}</span>
              </div>
              <p className="text-[10px] text-slate-500">Avg Rating</p>
            </div>
            <div className="p-2 bg-green-50 rounded-lg">
              <p className="text-lg font-bold text-green-700">{data.summary?.total_drivers || 0}</p>
              <p className="text-[10px] text-slate-500">Active Drivers</p>
            </div>
          </div>

          {/* Top Performer */}
          {data.summary?.top_performer && (
            <div className="flex items-center gap-2 p-2 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg border border-yellow-200">
              <Trophy className="h-5 w-5 text-yellow-500" />
              <span className="text-sm">
                <span className="font-medium">Top Performer:</span>{' '}
                <span className="text-yellow-700">{data.summary.top_performer}</span>
              </span>
            </div>
          )}

          {/* Driver List */}
          <div className="space-y-2">
            {data.drivers?.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No delivery data yet</p>
            ) : (
              data.drivers?.map((driver, idx) => {
                const perfBadge = getPerformanceBadge(driver.performance_score);
                const isExpanded = expandedDriver === driver.id;
                
                return (
                  <div 
                    key={driver.id}
                    className="border rounded-lg overflow-hidden"
                  >
                    <div 
                      className="flex items-center justify-between p-2 bg-slate-50 cursor-pointer hover:bg-slate-100"
                      onClick={() => setExpandedDriver(isExpanded ? null : driver.id)}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                          idx === 1 ? 'bg-slate-200 text-slate-700' :
                          idx === 2 ? 'bg-orange-100 text-orange-700' :
                          'bg-slate-100 text-slate-500'
                        }`}>
                          {idx + 1}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-800">{driver.name}</p>
                          <div className="flex items-center gap-1">
                            {getRatingStars(driver.average_rating)}
                            <span className="text-[10px] text-slate-500 ml-1">{driver.average_rating}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={`text-[10px] ${perfBadge.color}`}>
                          {perfBadge.label}
                        </Badge>
                        {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </div>
                    </div>
                    
                    {isExpanded && (
                      <div className="p-2 bg-white border-t space-y-2">
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="flex items-center gap-1">
                            <Truck className="h-3 w-3 text-blue-500" />
                            <span>{driver.total_deliveries} total deliveries</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3 text-green-500" />
                            <span>{driver.on_time_rate}% on-time</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Star className="h-3 w-3 text-yellow-500 fill-current" />
                            <span>{driver.five_star_count} five-star</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Target className="h-3 w-3 text-indigo-500" />
                            <span>Score: {driver.performance_score}</span>
                          </div>
                        </div>
                        {driver.today_deliveries > 0 && (
                          <p className="text-[10px] text-green-600">
                            {driver.today_deliveries} deliveries today
                          </p>
                        )}
                        {driver.low_rating_count > 0 && (
                          <p className="text-[10px] text-red-500">
                            ⚠️ {driver.low_rating_count} low ratings
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Driver's own view
  return (
    <Card className="h-full overflow-hidden" data-testid="driver-performance-widget">
      <CardHeader className="bg-gradient-to-r from-green-600 to-green-700 text-white py-3 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Award className="h-5 w-5" />
            <CardTitle className="text-base font-bold">My Performance</CardTitle>
          </div>
          <button onClick={fetchPerformance} className="p-1 hover:bg-white/20 rounded">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <div className="flex gap-2 mt-2">
          {['week', 'month', 'all'].map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-2 py-0.5 text-xs rounded-full transition-colors ${
                period === p 
                  ? 'bg-white text-green-700' 
                  : 'bg-white/20 hover:bg-white/30'
              }`}
            >
              {p === 'week' ? 'Week' : p === 'month' ? 'Month' : 'All'}
            </button>
          ))}
        </div>
      </CardHeader>

      <CardContent className="p-3 space-y-3 max-h-80 overflow-y-auto">
        {/* Main Stats */}
        <div className="grid grid-cols-2 gap-2">
          <div className="p-3 bg-green-50 rounded-lg text-center">
            <p className="text-2xl font-bold text-green-700">{data?.total_deliveries || 0}</p>
            <p className="text-xs text-slate-500">Deliveries</p>
          </div>
          <div className="p-3 bg-yellow-50 rounded-lg text-center">
            <div className="flex items-center justify-center gap-1">
              <Star className="h-5 w-5 text-yellow-400 fill-current" />
              <span className="text-2xl font-bold text-yellow-700">{data?.average_rating || 0}</span>
            </div>
            <p className="text-xs text-slate-500">Avg Rating</p>
          </div>
        </div>

        {/* Rating Breakdown */}
        {data?.rating_breakdown && (
          <div className="p-2 bg-slate-50 rounded-lg">
            <p className="text-xs font-medium text-slate-600 mb-2">Rating Breakdown</p>
            <div className="space-y-1">
              {[5, 4, 3, 2, 1].map((stars) => {
                const key = ['one_star', 'two_star', 'three_star', 'four_star', 'five_star'][stars - 1];
                const count = data.rating_breakdown[key] || 0;
                const total = data.total_deliveries || 1;
                const pct = (count / total) * 100;
                
                return (
                  <div key={stars} className="flex items-center gap-2 text-xs">
                    <span className="w-8 text-slate-500">{stars}★</span>
                    <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-yellow-400 rounded-full"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="w-6 text-right text-slate-500">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Store Breakdown */}
        {data?.stores?.length > 0 && (
          <div>
            <p className="text-xs font-medium text-slate-600 mb-2">Deliveries by Store</p>
            <div className="space-y-1">
              {data.stores.slice(0, 5).map((store, idx) => (
                <div 
                  key={idx}
                  className="flex items-center justify-between p-1.5 bg-slate-50 rounded text-xs"
                >
                  <span className="text-slate-700">{store.store}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500">{store.deliveries} trips</span>
                    <div className="flex items-center">
                      <Star className="h-3 w-3 text-yellow-400 fill-current" />
                      <span className="text-slate-600">{store.avg_rating}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Feedback */}
        {data?.recent_feedback?.length > 0 && (
          <div>
            <p className="text-xs font-medium text-slate-600 mb-2">Recent Feedback</p>
            <div className="space-y-1">
              {data.recent_feedback.slice(0, 3).map((fb, idx) => (
                <div key={idx} className="p-2 bg-blue-50 rounded text-xs">
                  <div className="flex items-center gap-1 mb-1">
                    <span className="text-blue-700 font-medium">{fb.store}</span>
                    <div className="flex">{getRatingStars(fb.rating)}</div>
                  </div>
                  <p className="text-slate-600 italic">"{fb.feedback}"</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {(!data?.total_deliveries || data.total_deliveries === 0) && (
          <div className="text-center py-6">
            <Truck className="h-10 w-10 mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-500">No deliveries yet</p>
            <p className="text-xs text-slate-400">Complete deliveries to see your stats</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
